import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, MessageCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authAPI } from '../../services/api';
import ApprovalPendingModal from './ApprovalPendingModal';
import toast from 'react-hot-toast';

interface LoginFormProps {
  onSwitchToSignup: () => void;
  onClose: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignup, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // 기본값 true (3일 자동 로그인)
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  
  const { setAuth, setLoading, isLoading } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const response = await authAPI.login(formData.email, formData.password);
      const { user, accessToken, refreshToken } = response.data;
      
      setAuth(user, accessToken, refreshToken, rememberMe);
      console.log('✅ 로그인 성공 - 자동 로그인:', rememberMe ? '3일' : '1일');

      if (user.mustChangePassword) {
        toast.success('임시 비밀번호가 감지되어 비밀번호 변경 화면으로 이동합니다.', { duration: 2500 });
        onClose();
        navigate('/force-password-change');
        return;
      }
      
      // 성공 메시지 표시
      toast.success(`환영합니다, ${user.name}님! 👋`, {
        duration: 2000,
        style: {
          background: '#f0fdf4',
          color: '#166534',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });
      
      onClose();
    } catch (error: any) {
      console.log('🔍 로그인 에러 디버깅:', error);
      console.log('🔍 에러 응답:', error.response);
      console.log('🔍 에러 데이터:', error.response?.data);
      console.log('🔍 에러 상태:', error.response?.status);
      console.log('🔍 requiresApproval 플래그:', error.response?.data?.requiresApproval);
      
      const errorData = error.response?.data;
      const statusCode = error.response?.status;
      
      // 관리자 승인 필요 메시지 처리
      if (errorData?.requiresApproval) {
        console.log('🚨 승인 대기 모달 표시:', errorData.message);
        console.log('🚨 모달 상태 설정:', { showApprovalModal: true, message: errorData.message });
        // 승인 대기 모달 표시
        setApprovalMessage(errorData.message);
        setShowApprovalModal(true);
        console.log('🚨 모달 상태 설정 완료');
      } else {
        // 사용자 친화적 에러 메시지 생성
        let errorMessage = '로그인에 실패했습니다.';
        
        if (statusCode === 401) {
          if (errorData?.message?.includes('이메일') || errorData?.message?.includes('비밀번호')) {
            errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
          } else {
            errorMessage = '인증에 실패했습니다. 이메일과 비밀번호를 확인해주세요.';
          }
        } else if (statusCode === 404) {
          errorMessage = '존재하지 않는 계정입니다. 회원가입을 진행해주세요.';
        } else if (statusCode === 403) {
          errorMessage = '계정이 비활성화되었습니다. 관리자에게 문의해주세요.';
        } else if (statusCode >= 500) {
          errorMessage = '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
        
        console.log('❌ 일반 에러 처리:', errorMessage);
        
        // 토스트 알림으로 에러 메시지 표시
        console.log('🍞 토스트 호출 시작:', errorMessage);
        try {
          toast.error(errorMessage, {
            duration: 4000,
            icon: <AlertCircle className="w-5 h-5 text-red-500" />,
            style: {
              background: '#fef2f2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
            },
          });
          console.log('✅ 토스트 호출 완료');
        } catch (toastError) {
          console.error('❌ 토스트 호출 실패:', toastError);
        }
        
        // 폼 에러 상태도 설정
        setErrors({
          general: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKakaoLogin = () => {
    // 카카오 클라이언트 ID 확인
    const kakaoClientId = import.meta.env.VITE_KAKAO_CLIENT_ID;
    
    if (!kakaoClientId) {
      console.error('❌ VITE_KAKAO_CLIENT_ID가 설정되지 않았습니다.');
      alert('카카오 로그인 설정 오류가 발생했습니다. 관리자에게 문의해주세요.');
      return;
    }
    
    // 카카오 로그인 URL로 리다이렉트
    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${kakaoClientId}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/kakao/callback')}&response_type=code`;
    console.log('🔑 카카오 로그인 URL 생성:', { clientId: kakaoClientId?.substring(0, 10) + '...', redirectUri: window.location.origin + '/auth/kakao/callback' });
    window.location.href = kakaoAuthUrl;
  };

  return (
    <div className="w-full max-w-md mx-auto p-4 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg p-2">
          <img 
            src="/images/logo.png" 
            alt="e2b 로고" 
            className="w-full h-full object-contain"
          />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">로그인</h2>
        <p className="text-sm sm:text-base text-slate-600">eat bible everyday에 오신 것을 환영합니다</p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
            이메일
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm sm:text-base"
              placeholder="이메일을 입력하세요"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
            비밀번호
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full pl-10 pr-12 py-2.5 sm:py-3 border border-slate-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all text-sm sm:text-base"
              placeholder="비밀번호를 입력하세요"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* 자동 로그인 체크박스 */}
        <div className="flex items-center justify-between">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="sr-only"
            />
            <div className={`relative w-4 h-4 rounded border-2 transition-all ${
              rememberMe 
                ? 'bg-rose-500 border-rose-500' 
                : 'bg-white border-slate-300 hover:border-rose-300'
            }`}>
              {rememberMe && (
                <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5 transform -translate-x-0.5 -translate-y-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <span className="ml-2 text-sm text-slate-600">
              자동 로그인 (3일간 유지)
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm sm:text-base"
        >
          {isLoading ? '로그인 중...' : '로그인'}
        </button>
      </form>

      <div className="my-4 sm:my-6 flex items-center">
        <div className="flex-1 border-t border-slate-300"></div>
        <span className="px-3 sm:px-4 text-xs sm:text-sm text-slate-500">또는</span>
        <div className="flex-1 border-t border-slate-300"></div>
      </div>

      <button
        onClick={handleKakaoLogin}
        className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-800 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 sm:gap-3 shadow-lg text-sm sm:text-base"
      >
        <MessageCircle size={18} />
        카카오로 간편 로그인
      </button>

      <div className="mt-4 sm:mt-6 text-center">
        <p className="text-slate-600 text-sm sm:text-base">
          아직 계정이 없으신가요?{' '}
          <button
            onClick={onSwitchToSignup}
            className="text-rose-500 hover:text-rose-600 font-semibold"
          >
            회원가입
          </button>
        </p>
      </div>
      
      {/* 승인 대기 모달 */}
      <ApprovalPendingModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        message={approvalMessage}
      />
    </div>
  );
};

export default LoginForm;
